import type { CatalogEntry } from '../types';

/**
 * Reports (feature `07`) error codes + catalog.
 *
 * Code shape is `07AAEE` (decimal, zero-padded):
 *   - `AA` = endpoint within the feature (`00` = feature-level / cross-endpoint).
 *   - `EE` = specific condition within that endpoint.
 *
 * Endpoint → `AA` map (ReportsController):
 *   getOverview=01, getAccountStats=02, getLeaderboard=03, getTrends=04,
 *   getDeliverability=05, warmup ESP analytics=06, emailEspExportReport=07,
 *   warmup-export=08, resyncWarmupEmails=09.
 *
 * getAccountStats raises ONE `AccountNotFound` for both the genuine miss and the
 * IDOR case (account belongs to another workspace) — both surface to the user as
 * the same condition, so they collapse to a single code. getOverview,
 * getLeaderboard and getDeliverability have no throw sites (they return empty
 * shapes for empty data), so reserve no codes today.
 */
export const REPORTS_CODES = {
  /** getAccountStats=02. */
  AccountStats: {
    AccountNotFound: '070201',
  },
  /** getTrends=04 (date-range validation). */
  Trends: {
    InvalidDateFormat: '070401',
    FromAfterTo: '070402',
    RangeTooLarge: '070403',
  },
  /**
   * Warmup ESP analytics=06 — ONE set of codes for all four
   * `/reports/warmup/esp/*` routes, because they share one request-validation
   * path (window + timezone) rather than having per-route conditions.
   *
   * Deliberately no `AccountNotFound`: these routes do no MySQL lookup at all
   * (spec §9.0a). Authorization is the `workspaceId` predicate inside the
   * ClickHouse query, so a foreign account id returns zeroes, not a 404 — which
   * also means it cannot be used to enumerate account ids in other tenants.
   */
  EspAnalytics: {
    InvalidDateFormat: '070601',
    FromAfterTo: '070602',
    RangeTooLarge: '070603',
    InvalidTimeZone: '070604',
  },
  /**
   * emailEspExportReport=07 (the "Download report" CSV export). Reuses
   * `EspAnalytics`'s window-validation codes via the shared
   * `resolveEspWindow` (no separate copies here) and `AccountStats`'s
   * `AccountNotFound` for the ownership gate — this group holds only the one
   * condition unique to the export: the SMTP send itself failing after a
   * valid, owned request produced a report.
   */
  EspExport: {
    EmailSendFailed: '070701',
  },
  /**
   * emailAllAccountsWarmupExport=08 (the listing page's "Export report"
   * button — a CSV of every account in the current filter/selection scope,
   * emailed to the requester). Selection resolution reuses
   * `EmailAccounts.BulkOps.EmptySelection` verbatim (see
   * `EmailAccountsRepository.resolveSelectionIds`) rather than duplicating
   * that condition under a Reports-prefixed code — this group holds only the
   * one condition unique to the export itself: the SMTP send failing after a
   * valid selection produced a report. Mirrors `EspExport.EmailSendFailed`
   * exactly in shape, but NOT in how it reaches a caller: this export moved
   * onto an async queue (`reports-warmup-export`, `@trulyinbox/queue`) once
   * its selection cap was removed, so `EmailSendFailed` is now thrown inside
   * `ReportsService.processAllAccountsWarmupExportJob` (background job),
   * never inside an HTTP request — the `httpStatus: 502` below is retained
   * for the `AppException` shape/logging, but no live route actually renders
   * it as a response anymore. `SelectionTooLarge` no longer applies to this
   * endpoint at all (selection is uncapped; see
   * `apps/api/src/modules/reports/CLAUDE.md`).
   *
   * `InvalidDateFormat`/`FromAfterTo`/`RangeTooLarge` back the endpoint's
   * custom `from`/`to` range option (60/90-day presets need no validation
   * beyond `@IsIn`, since they're fixed values). Unlike `EmailSendFailed`
   * above, these ARE thrown synchronously, before `queue.add` —
   * `ReportsService.resolveExportRange` runs as the first line of
   * `enqueueAllAccountsWarmupExport`, so a bad range still renders as a real
   * HTTP 400, the same as `EmailAccounts.BulkOps.EmptySelection`. Wording
   * mirrors `EspAnalytics`'s siblings (`070601`-`070603`), but this group
   * keeps its own copies rather than reusing them — `AllAccountsExport` and
   * `EspAnalytics` are separate endpoint slots (`08` vs `06`) with
   * independent validation paths (this one has no timezone dimension).
   */
  AllAccountsExport: {
    EmailSendFailed: '070801',
    InvalidDateFormat: '070802',
    FromAfterTo: '070803',
    RangeTooLarge: '070804',
  },
  /**
   * resyncWarmupEmails=09 (`POST /reports/warmup/resync`) — the cron/ops
   * endpoint that re-syncs `warmupEmailHistory` (MySQL, the system of record)
   * into `warmupEmails` (ClickHouse).
   *
   * ONE code, and deliberately no `AccountNotFound`. This route carries no user
   * session (`CronValidationGuard`), so there is no tenant boundary for an
   * ownership gate to protect — the only thing such a check would add is typo
   * detection, and it would cost the legitimate case of resyncing a
   * soft-deleted mailbox's history. An unknown id reports `scanned: 0` instead,
   * which is unambiguous to the operator reading the response. Malformed dates
   * never reach a code either: `@IsISO8601` rejects them at the global
   * `ValidationPipe`, unlike `Trends`/`EspAnalytics` whose windows arrive as
   * bare strings. Ordering is the one thing class-validator cannot express
   * across two fields.
   */
  WarmupResync: {
    FromAfterTo: '070901',
  },
} as const;

export const REPORTS_CATALOG: Record<string, CatalogEntry> = {
  // ── getAccountStats ──
  '070201': { httpStatus: 404, message: "We couldn't find that email account." },

  // ── getTrends ──
  '070401': {
    httpStatus: 400,
    message: 'Please enter valid dates in YYYY-MM-DD format.',
  },
  '070402': {
    httpStatus: 400,
    message: 'The start date must be on or before the end date.',
  },
  '070403': {
    httpStatus: 400,
    message: "Your date range can't be longer than 365 days.",
  },

  // ── warmup ESP analytics ──
  '070601': {
    httpStatus: 400,
    message: 'Please enter valid dates in YYYY-MM-DD format.',
  },
  '070602': {
    httpStatus: 400,
    message: 'The start date must be on or before the end date.',
  },
  '070603': {
    httpStatus: 400,
    message: "Your date range can't be longer than 90 days.",
  },
  '070604': {
    httpStatus: 400,
    message: "We don't recognise that time zone.",
  },

  // ── emailEspExportReport ──
  '070701': {
    httpStatus: 502,
    message: "We couldn't send your report email. Please try again.",
  },

  // ── emailAllAccountsWarmupExport ──
  '070801': {
    httpStatus: 502,
    message: "We couldn't send your report email. Please try again.",
  },
  '070802': {
    httpStatus: 400,
    message: 'Please enter valid dates in YYYY-MM-DD format.',
  },
  '070803': {
    httpStatus: 400,
    message: 'The start date must be on or before the end date.',
  },
  '070804': {
    httpStatus: 400,
    message: "Your date range can't be longer than 90 days.",
  },

  // ── resyncWarmupEmails ──
  '070901': {
    httpStatus: 400,
    message: 'The start date must be on or before the end date.',
  },
};
